/* eslint-disable no-underscore-dangle */
import { createStore, createHook } from 'react-sweet-state';
import axios from '../../_actions/axiosInstance';
import config from '../../_config/config';
import { getUserData } from '../../helpers/localStorage';

// Module-level (not React context) so in-progress work survives navigating between steps.
const CURRENT_YEAR = new Date().getFullYear();

export const HEADER_VALUES = {
  'data.inspection.site.name': 'Arkansas Test Site',
  'site.facility_id': 'AR-10234',
  'data.inspection.site.address': '500 Woodlane St',
  'data.inspection.site.city': 'Little Rock',
  'data.inspection.site.state': 'AR',
  'user.first_name': 'Jane',
  'user.last_name': 'Doe',
  'client.name': 'Love’s Travel Stops',
  'calendar:year': String(CURRENT_YEAR)
};

// Not a stored field -- always the real current year, resolved client-side.
const CURRENT_YEAR_ITEM = { key: 'calendar:year', label: `Current Year (${CURRENT_YEAR})` };

// Not prompt-scoped: dragged once per available row, and the backend fills the rows in order.
const COMMENT_ITEM = { key: 'inspection_comment', label: 'Comment' };

// Zipped against the same row order as Comment.
const PHOTO_ITEM = { key: 'inspection_photo', label: 'Remedy/Resolve' };
const DATE_OF_INSPECTION_ITEM = { key: 'inspection_row_date', label: 'Date of Inspection (row)' };

// The per-row tags, paired with the payload key each one is sent under. Adding a fourth is a
// line here rather than an edit in the filter, the build and the returned object.
const ROW_TAGS = [
  [COMMENT_ITEM, 'comment'],
  [PHOTO_ITEM, 'photo'],
  [DATE_OF_INSPECTION_ITEM, 'date_of_inspection']
];

// Inside InspectionResult.data rather than declared model fields, so general_information
// can't return them. The signature ids are drawn as embedded images, not text.
const REPRESENTATIVE_NAME_ITEM = { key: 'data.representative_name', label: 'Representative Name' };
const INSPECTOR_SIGNATURE_ITEM = { key: 'data.signature.inspector', label: 'Inspector Signature' };
const REP_SIGNATURE_ITEM = { key: 'data.signature.rep', label: 'Representative Signature' };
const CERTIFIED_ITEM = { key: 'data.certified', label: 'Certified' };

// Derived from inspector_name -- nothing stores initials.
const INSPECTOR_INITIALS_ITEM = { key: 'computed:inspector_initials', label: 'Inspector Initials' };

// Plain names for keys whose generic "<Collection> — <Field>" label reads as noise.
const LABEL_OVERRIDES = {
  'inspection_result.created_on': 'Date of Inspection'
};

const humanizeWords = str => str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

// This backend returns {"errors": [...]}, not the {"error": "..."} most places read.
export const extractErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors)) return data.errors.join(', ');
  return data?.error || fallback;
};

export const extractErrorMessageAsync = async (error, fallback) => {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (Array.isArray(parsed?.errors)) return parsed.errors.join(', ');
      return parsed?.error || fallback;
    } catch {
      return fallback;
    }
  }
  return extractErrorMessage(error, fallback);
};

// The collection prefix disambiguates names that collide across models (Site.name vs Client.name).
const humanizeLabel = (collection, attrName) =>
  `${humanizeWords(collection)} — ${humanizeWords(attrName)}`;

// An AcroForm field is addressed by name; a drawn box by its top-left point in PDF points
// (bottom-left origin), converted from the pixel rect using its captured render scale.
const buildFieldTarget = async (field, pdfDoc, pageHeightCache) => {
  if (!pageHeightCache.has(field.page)) {
    const page = await pdfDoc.getPage(field.page);
    pageHeightCache.set(field.page, page.getViewport({ scale: 1 }).height);
  }
  const pageHeightPt = pageHeightCache.get(field.page);
  const scale = field.scale || 1;
  const xPt = field.x / scale;
  const yPt = pageHeightPt - (field.y + field.h) / scale;
  const point = { x: Math.round(xPt), y: Math.round(yPt) };

  // An AcroForm field carries its own rect, so it gets coordinates too. Without them
  // the backend's x/y default to 0 and any fallback stamp lands in the page corner.
  if (field.source === 'acro') {
    return { type: 'acroform', field: field.name, page: field.page - 1, ...point };
  }
  return { type: 'coordinate', page: field.page - 1, ...point };
};

const initialState = {
  file: null,
  formState: '',
  formStateId: '',
  formYear: '',
  formName: '',
  fields: [],

  // Only states this database can resolve -- upload 400s on any it can't.
  stateOptions: [],
  stateOptionsLoading: false,
  stateOptionsError: '',

  // Set once the upload succeeds; the mapping save and testing preview both need it.
  stateFormId: '',
  uploadLoading: false,
  answerFormat: 'triple',
  passMark: '',
  failMark: '',
  naMark: '',
  inspectionType: '',
  // Must stay a plain integer string -- the backend's t.ToInt() rejects '1.0' outright.
  mappingVersion: '1',

  // Kept so returning to Mapping doesn't lose which panel/category/page was open.
  openPanelId: 'header-info',
  headerSearch: '',
  promptSearch: '',
  collapsedCategoryIds: [],
  mappingPageNum: 1,

  // Shared so Mapping and Review read the same fetched data without double-fetching.
  headerItems: [],
  headerItemsLoading: false,
  headerItemsError: '',

  passMarkOptions: [],
  failMarkOptions: [],
  naMarkOptions: [],
  inspectionTypeOptions: [],
  promptSettingsLoading: false,
  promptSettingsError: '',

  promptCategories: [],
  promptCategoriesLoading: false,
  promptCategoriesError: ''
};

const MappingToolStore = createStore({
  initialState,
  actions: {
    // A new file resets the mapping -- fields from the previous one are meaningless against it.
    setFile: file => ({ setState }) =>
      setState({ file, fields: [], mappingPageNum: 1, stateFormId: '' }),
    setFormState: (formState, formStateId) => ({ setState }) =>
      setState({ formState, formStateId }),
    setFormYear: formYear => ({ setState }) => setState({ formYear }),
    setFormName: formName => ({ setState }) => setState({ formName }),
    setFields: updater => ({ getState, setState }) => {
      const { fields } = getState();
      setState({ fields: typeof updater === 'function' ? updater(fields) : updater });
    },
    setAnswerFormat: answerFormat => ({ setState }) => setState({ answerFormat }),
    setPassMark: passMark => ({ setState }) => setState({ passMark }),
    setFailMark: failMark => ({ setState }) => setState({ failMark }),
    setNaMark: naMark => ({ setState }) => setState({ naMark }),
    // Changing the question set invalidates every prompt mapping: mappedKey carries the old
    // type's promptId/categoryId, which mean nothing under the new one. Drop them here rather
    // than let buildMappingPayload ship them under the new question_set. Header/comment/photo/
    // date fields are type-independent and survive. Drawn boxes go entirely; AcroForm boxes
    // belong to the PDF, so they only lose their mapping -- same split as removeField().
    setInspectionType: inspectionType => ({ getState, setState }) => {
      const { inspectionType: previous, fields } = getState();
      if (inspectionType === previous) return;
      setState({
        inspectionType,
        fields: fields
          .filter(f => !(f.mappedKey?.promptId && f.source === 'drawn'))
          .map(f => (f.mappedKey?.promptId ? { ...f, mappedKey: null, period: '' } : f))
      });
    },
    setMappingVersion: mappingVersion => ({ setState }) => setState({ mappingVersion }),
    setOpenPanelId: updater => ({ getState, setState }) => {
      const { openPanelId } = getState();
      setState({ openPanelId: typeof updater === 'function' ? updater(openPanelId) : updater });
    },
    setHeaderSearch: headerSearch => ({ setState }) => setState({ headerSearch }),
    setPromptSearch: promptSearch => ({ setState }) => setState({ promptSearch }),
    setCollapsedCategoryIds: updater => ({ getState, setState }) => {
      const { collapsedCategoryIds } = getState();
      setState({
        collapsedCategoryIds:
          typeof updater === 'function' ? updater(collapsedCategoryIds) : updater
      });
    },
    setMappingPageNum: updater => ({ getState, setState }) => {
      const { mappingPageNum } = getState();
      setState({
        mappingPageNum: typeof updater === 'function' ? updater(mappingPageNum) : updater
      });
    },

    // Returns the outcome rather than toasting -- notifying is the view's job. null means
    // there was nothing to delete.
    deleteStateForm: () => async ({ getState }) => {
      const { stateFormId } = getState();
      if (!stateFormId) return null;
      try {
        const url = config.api.api_url + config.api.mapping_tool.deleteStateForm(stateFormId);
        await axios.delete(url);
        return { ok: true, message: 'File deleted with success' };
      } catch {
        return { ok: false, message: 'Error deleting the file' };
      }
    },

    fetchStates: () => async ({ getState, setState }) => {
      const { stateOptions, stateOptionsLoading } = getState();
      if (stateOptions.length || stateOptionsLoading) return;
      setState({ stateOptionsLoading: true, stateOptionsError: '' });
      try {
        const url = config.api.api_url + config.api.mapping_tool.getStates;
        const res = await axios.get(url);
        setState({
          stateOptions: res.data.data.map(state => ({
            value: state.id,
            label: state.abbreviation,
            name: state.name
          })),
          stateOptionsLoading: false
        });
      } catch (error) {
        setState({
          stateOptionsLoading: false,
          stateOptionsError: extractErrorMessage(error, 'Could not load states.')
        });
      }
    },

    // Client comes from the logged-in user, never a picker -- an SME uploads for their own only.
    uploadStateForm: file => async ({ getState, setState }) => {
      const { formStateId, formYear, formName, mappingVersion } = getState();
      const client = getUserData()?.client;
      if (!client?.id) {
        return { ok: false, message: 'No client found for the current user.' };
      }

      setState({ uploadLoading: true });
      try {
        const body = new FormData();
        body.append('filename', file.name);
        body.append('name', formName);
        body.append('client_id', client.id);
        body.append('state_id', formStateId);
        body.append('year', formYear);
        body.append('version', mappingVersion || '1');
        body.append('file', file);

        const url = config.api.api_url + config.api.mapping_tool.uploadStateFormFile;
        const res = await axios.post(url, body);
        setState({ stateFormId: res?.data?._id, uploadLoading: false });
        return { ok: true, message: 'State form uploaded with success' };
      } catch (error) {
        setState({ uploadLoading: false });
        return {
          ok: false,
          message: extractErrorMessage(error, 'Could not upload the state form file.')
        };
      }
    },

    // { site: {db_field: attr_name}, client: {...}, user: {...} }, flattened into one dropdown.
    fetchGeneralInformation: () => async ({ getState, setState }) => {
      const { headerItems, headerItemsLoading } = getState();
      if (headerItems.length || headerItemsLoading) return;
      setState({ headerItemsLoading: true, headerItemsError: '' });
      try {
        const url = config.api.api_url + config.api.mapping_tool.getGeneralInformation;
        const res = await axios.get(url);
        const items = Object.entries(res.data.data)
          .flatMap(([collection, fields]) =>
            Object.entries(fields).map(([dbField, attrName]) => {
              const key = `${collection}.${dbField}`;
              return { key, label: LABEL_OVERRIDES[key] || humanizeLabel(collection, attrName) };
            })
          )
          .concat([
            CURRENT_YEAR_ITEM,
            COMMENT_ITEM,
            PHOTO_ITEM,
            DATE_OF_INSPECTION_ITEM,
            REPRESENTATIVE_NAME_ITEM,
            INSPECTOR_SIGNATURE_ITEM,
            REP_SIGNATURE_ITEM,
            CERTIFIED_ITEM,
            INSPECTOR_INITIALS_ITEM
          ])
          .sort((a, b) => a.label.localeCompare(b.label));
        setState({ headerItems: items, headerItemsLoading: false });
      } catch (error) {
        setState({
          headerItemsLoading: false,
          headerItemsError: extractErrorMessage(error, 'Could not load header fields.')
        });
      }
    },

    // GET prompt_settings -- Pass/Fail mark option lists plus the inspection type list.
    fetchPromptSettings: () => async ({ getState, setState }) => {
      const { inspectionTypeOptions, promptSettingsLoading } = getState();
      if (inspectionTypeOptions.length || promptSettingsLoading) return;
      setState({ promptSettingsLoading: true, promptSettingsError: '' });
      try {
        const url = config.api.api_url + config.api.mapping_tool.getPromptSettings;
        const res = await axios.get(url);
        setState({
          // `mark` is the literal value written onto the form; `label` is only display text.
          passMarkOptions: res.data.pass.map(mark => ({
            value: mark.id,
            label: mark.label,
            mark: mark.value
          })),
          failMarkOptions: res.data.fail.map(mark => ({
            value: mark.id,
            label: mark.label,
            mark: mark.value
          })),
          // Guarded -- the backend doesn't return this list yet; empty beats breaking Pass/Fail.
          naMarkOptions: (res.data.na || []).map(mark => ({
            value: mark.id,
            label: mark.label,
            mark: mark.value
          })),
          inspectionTypeOptions: res.data.inspection_question_set.map(type => ({
            value: type.id,
            label: type.name
          })),
          promptSettingsLoading: false
        });
      } catch (error) {
        setState({
          promptSettingsLoading: false,
          promptSettingsError: extractErrorMessage(error, 'Could not load prompt settings.')
        });
      }
    },

    // GET the real prompt catalog for one inspection type, grouped client-side by category
    // (the endpoint returns a flat list, each question carrying its own category reference).
    fetchInspectionQuestionSet: inspectionTypeId => async ({ setState }) => {
      if (!inspectionTypeId) {
        setState({ promptCategories: [], promptCategoriesError: '' });
        return;
      }
      setState({ promptCategoriesLoading: true, promptCategoriesError: '' });
      try {
        const url =
          config.api.api_url + config.api.mapping_tool.getInspectionQuestionSet(inspectionTypeId);
        const res = await axios.get(url);
        const categoriesById = new Map();
        res.data.data.forEach(question => {
          const categoryId = question.category?.id ?? 'uncategorized';
          const categoryName = question.category?.name ?? 'Uncategorized';
          if (!categoriesById.has(categoryId)) {
            categoriesById.set(categoryId, { id: categoryId, name: categoryName, prompts: [] });
          }
          const { _id: questionId, prompt: questionText } = question;
          categoriesById.get(categoryId).prompts.push({ id: questionId, text: questionText });
        });
        setState({
          promptCategories: Array.from(categoriesById.values()),
          promptCategoriesLoading: false
        });
      } catch (error) {
        setState({
          promptCategoriesLoading: false,
          promptCategoriesError: extractErrorMessage(error, 'Could not load prompt catalog.')
        });
      }
    },

    // Body of the new_mapped_state_form_pdf POST. `answer` is the semantic outcome
    // ('pass'/'fail'/'na'), not the literal mark -- the backend matches it against the real
    // status, then looks the glyph up separately in answer_marks.
    buildMappingPayload: pdfDoc => async ({ getState }) => {
      const {
        fields,
        inspectionType,
        passMark,
        failMark,
        naMark,
        passMarkOptions,
        failMarkOptions,
        naMarkOptions,
        stateFormId
      } = getState();
      const pageHeightCache = new Map();
      const mappedFields = fields.filter(f => f.mappedKey);

      const rowTagKeys = ROW_TAGS.map(([item]) => item.key);
      const headerFields = mappedFields.filter(
        f => f.mappedKey.key && !rowTagKeys.includes(f.mappedKey.key)
      );
      // typeId as well as promptId: a mapping placed under a different question set must never
      // be serialized under this one's `question_set`. setInspectionType clears these, so this
      // is the belt to that braces -- it also matches countMappedForOutcome's UI-side filter.
      const promptFields = mappedFields.filter(
        f => f.mappedKey.promptId && f.mappedKey.typeId === inspectionType
      );

      // Targets per key, not one target -- a plain {key: target} would silently keep only the
      // last placement of a key dropped more than once.
      const header = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const field of headerFields) {
        const { key } = field.mappedKey;
        if (!header[key]) header[key] = { targets: [] };
        // eslint-disable-next-line no-await-in-loop
        const target = await buildFieldTarget(field, pdfDoc, pageHeightCache);
        header[key].targets.push({ target, period: field.period || null });
      }

      // Flat, ordered row positions -- the backend zips its own row order against these.
      const buildRowTargets = async rowFields => {
        const targets = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const field of rowFields) {
          // eslint-disable-next-line no-await-in-loop
          const target = await buildFieldTarget(field, pdfDoc, pageHeightCache);
          targets.push({ target });
        }
        return targets;
      };
      const rowTagPayload = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const [item, payloadKey] of ROW_TAGS) {
        rowTagPayload[payloadKey] = {
          // eslint-disable-next-line no-await-in-loop
          targets: await buildRowTargets(mappedFields.filter(f => f.mappedKey.key === item.key))
        };
      }

      const passMarkLiteral = passMarkOptions.find(o => o.value === passMark)?.mark;
      const failMarkLiteral = failMarkOptions.find(o => o.value === failMark)?.mark;
      const naMarkLiteral = naMarkOptions.find(o => o.value === naMark)?.mark;
      const answerMarks = {};
      // !== undefined, not truthiness: '' is a deliberate choice meaning "mark nothing
      // for this outcome", and a falsy check would drop it before it was ever sent.
      if (passMarkLiteral !== undefined) answerMarks.pass = passMarkLiteral;
      if (failMarkLiteral !== undefined) answerMarks.fail = failMarkLiteral;
      if (naMarkLiteral !== undefined) answerMarks.na = naMarkLiteral;

      const categories = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const field of promptFields) {
        const { categoryId, promptId, outcome } = field.mappedKey;
        if (!categories[categoryId]) categories[categoryId] = {};
        if (!categories[categoryId][promptId]) categories[categoryId][promptId] = { targets: [] };
        // eslint-disable-next-line no-await-in-loop
        const target = await buildFieldTarget(field, pdfDoc, pageHeightCache);
        // period disambiguates repeated placements; omitted for a single-cell prompt.
        // The P/F handle is one drop in the UI but two outcomes on the page, so it
        // expands into a pass and a fail entry sharing the point. 'any' is not usable
        // here -- it also matches n/a, which these forms give its own column.
        const outcomes = outcome === 'passfail' ? ['pass', 'fail'] : [outcome];
        // eslint-disable-next-line no-restricted-syntax
        for (const answer of outcomes) {
          categories[categoryId][promptId].targets.push({
            target,
            answer,
            period: field.period || null
          });
        }
      }

      return {
        state_form_id: stateFormId || null,
        // No `version` -- it's fixed at upload. `inspectionType` is really a question set id
        // (see fetchPromptSettings), sent under its real name so the backend derives the type.
        question_set: inspectionType || null,
        answer_marks: answerMarks,
        header,
        categories,
        // comment, photo and date_of_inspection -- see ROW_TAGS.
        ...rowTagPayload
      };
    },

    clearMapping: () => ({ setState }) => setState(initialState)
  },
  name: 'MappingToolStore'
});

const useMappingToolStore = createHook(MappingToolStore);

export default useMappingToolStore;
